import { useCallback, useEffect, useRef, useState } from 'react';
import {
	PlayIcon,
	PauseIcon,
	VolumeIcon,
	MuteIcon,
	MaximizeIcon,
	MinimizeIcon,
	SettingsIcon,
	BackThirtyIcon,
	BackSixtyIcon,
} from '@playerIcons';
import { Button, makeStyles } from '@material-ui/core';
import { debounce } from 'lodash';
import { Check } from '@material-ui/icons';

const useStyles = makeStyles({
	menuItem: {
		'&:hover': {
			backgroundColor: 'rgba(255, 255, 255, 0.2)',
		},
	},
	customMenu: {
		position: 'absolute',
		backgroundColor: 'rgba(0, 0, 0, 0.8)',
		color: 'white',
		padding: '8px 0',
		borderRadius: '4px',
		bottom: '40px',
		right: '0',
		minWidth: '150px',
		zIndex: 1500,
	},
	menuOption: {
		padding: '8px 16px',
		cursor: 'pointer',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		'&:hover': {
			backgroundColor: 'rgba(255, 255, 255, 0.2)',
		},
	},
	menuCheckIcon: {
		marginRight: '8px',
	},
});

const playbackSpeeds = [
	{ name: '1x', value: 100 },
	{ name: '1.5x', value: 150 },
	{ name: '2x', value: 200 },
];

const KinesisHistoryControls = ({
	room,
	playerRef,
	boxRef,
	live,
	handleIphoneFullScreenToggle,
	showControls = true,
	setIsFullscreen,
	startTime,
}) => {
	const classes = useStyles();
	const volumeRef = useRef(null);
	const timeElapsedRef = useRef(null);
	const durationRef = useRef(null);
	const progressBarRef = useRef(null);
	const seekRef = useRef(null);
	const seekTooltipRef = useRef(null);
	const fullScreenRef = useRef(null);
	const fullscreenIconRef = useRef(null);
	const fullscreenExitIconRef = useRef(null);
	const isIphone = /iPhone|iPod/.test(navigator.userAgent);
	const [isSpeedMenuOpen, setIsSpeedMenuOpen] = useState(false);
	const [playbackSpeed, setPlaybackSpeed] = useState(100);
	const [volume, setVolume] = useState(0.5);
	const [displayStartTime, setDisplayStartTime] = useState(null);
	const startTimeRef = useRef(null);
	const videoDurationRef = useRef(null);

	const toggleSpeedMenu = () => {
		setIsSpeedMenuOpen((prev) => !prev);
	};

	const handleSpeedSelect = (speedValue) => {
		setPlaybackSpeed(speedValue);
		setIsSpeedMenuOpen(false);
		const video = playerRef.current;
		if (video) {
			video.playbackRate = speedValue / 100;
		}
	};

	useEffect(() => {
		let interval;
		const video = playerRef.current;

		if (live && video) {
			interval = setInterval(() => {
				initializeVideo();
			}, 2000);
		}

		video?.addEventListener('click', togglePlay);

		if (isIphone) {
			fullScreenRef.current?.addEventListener('click', handleIphoneFullScreenToggle);
		} else {
			fullScreenRef.current?.addEventListener('click', toggleFullScreen);
		}

		boxRef.current?.addEventListener('fullscreenchange', updateFullscreenButton);
		seekRef.current?.addEventListener('mousemove', updateSeekTooltip);
		seekRef.current?.addEventListener('input', skipAhead);
		video?.addEventListener('loadedmetadata', initializeVideo);
		video?.addEventListener('timeupdate', updateTimeElapsed);
		video?.addEventListener('timeupdate', updateProgress);

		return () => {
			video?.removeEventListener('click', togglePlay);
			if (isIphone) {
				fullScreenRef.current?.removeEventListener('click', handleIphoneFullScreenToggle);
			} else {
				fullScreenRef.current?.removeEventListener('click', toggleFullScreen);
			}
			boxRef.current?.removeEventListener('fullscreenchange', updateFullscreenButton);
			seekRef.current?.removeEventListener('mousemove', updateSeekTooltip);
			seekRef.current?.removeEventListener('input', skipAhead);
			clearInterval(interval);
		};
	}, [room?.id, playerRef.current]);

	useEffect(() => {
		const handleFullscreenChange = () => {
			if (setIsFullscreen) {
				if (!document.fullscreenElement) {
					setIsFullscreen(false);
				} else {
					setIsFullscreen(true);
				}
			}
		};

		document.addEventListener('fullscreenchange', handleFullscreenChange);

		return () => {
			document.removeEventListener('fullscreenchange', handleFullscreenChange);
		};
	}, []);

	function togglePlay() {
		const video = playerRef.current;
		if (video) {
			if (video.paused || video.ended) {
				video.play();
			} else {
				video.pause();
			}
		}
	}

	function updateVolume(event) {
		const newVolume = parseFloat(event.target.value);
		const video = playerRef.current;
		if (video) {
			if (video.muted && newVolume > 0) {
				video.muted = false;
			}
			video.volume = newVolume;
			setVolume(newVolume);
		}
	}

	function toggleMute() {
		const video = playerRef.current;
		if (video) {
			if (video.muted || video.volume === 0) {
				video.muted = false;
				video.volume = 0.5;
				setVolume(0.5);
			} else {
				video.muted = true;
				video.volume = 0;
				setVolume(0);
			}
		}
	}

	function toggleFullScreen() {
		if (document.fullscreenElement) {
			document.exitFullscreen();
		} else if (document.webkitFullscreenElement) {
			document.webkitExitFullscreen();
		} else if (boxRef.current.requestFullscreen) {
			boxRef.current.requestFullscreen();
		} else if (boxRef.current.webkitRequestFullscreen) {
			boxRef.current.webkitRequestFullscreen();
		} else if (boxRef.current.msRequestFullscreen) {
			boxRef.current.msRequestFullscreen();
		}
	}

	function updateFullscreenButton() {
		if (document.fullscreenElement) {
			fullscreenIconRef.current?.classList.add('hidden');
			fullscreenExitIconRef.current?.classList.remove('hidden');
			fullScreenRef.current?.setAttribute('data-title', 'Exit full screen (f)');
		} else {
			fullscreenIconRef.current?.classList.remove('hidden');
			fullscreenExitIconRef.current?.classList.add('hidden');
			fullScreenRef.current?.setAttribute('data-title', 'Full screen (f)');
		}
	}

	function formatTime12Hour(date) {
		let hours = date.getHours();
		const minutes = date.getMinutes();
		const seconds = date.getSeconds();
		const ampm = hours >= 12 ? 'pm' : 'am';
		hours = hours % 12;
		hours = hours ? hours : 12;
		const minutesStr = minutes < 10 ? `0${minutes}` : minutes;
		const secondsStr = seconds < 10 ? `0${seconds}` : seconds;
		return `${hours}:${minutesStr}:${secondsStr}${ampm}`;
	}

	function updateEndTime() {
		if (startTimeRef.current && videoDurationRef.current) {
			const endDate = new Date(startTimeRef.current.getTime() + videoDurationRef.current * 1000);
			const formattedEndTime = formatTime12Hour(endDate);

			if (durationRef.current) {
				durationRef.current.innerText = formattedEndTime;
				durationRef.current.setAttribute('datetime', formattedEndTime);
			}
		}
	}

	function initializeVideo() {
		const video = playerRef.current;
		setPlaybackSpeed(100);
		if (video) {
			const videoDuration = Math.round(video.duration);
			videoDurationRef.current = videoDuration;
			if (seekRef.current) {
				seekRef.current.setAttribute('max', videoDuration);
			}
			if (progressBarRef.current) {
				progressBarRef.current.setAttribute('max', videoDuration);
			}
			updateEndTime();
		}
	}

	function updateTimeElapsed() {
		const video = playerRef.current;
		if (video && startTimeRef.current) {
			const currentTimeInSeconds = Math.round(video?.currentTime || 0);
			const elapsedDate = new Date(startTimeRef.current.getTime() + currentTimeInSeconds * 1000);
			const formattedTime = formatTime12Hour(elapsedDate);

			if (timeElapsedRef.current) {
				timeElapsedRef.current.innerText = formattedTime;
				timeElapsedRef.current.setAttribute('datetime', formattedTime);
			}

			updateProgress();
		}
	}

	function updateProgress() {
		const video = playerRef.current;
		if (video) {
			if (progressBarRef.current) {
				seekRef.current.value = Math.floor(video?.currentTime || 0);
			}
			if (progressBarRef.current) {
				progressBarRef.current.value = Math.floor(video?.currentTime || 0);
			}
		}
	}

	function updateSeekTooltip(event) {
		if (!startTimeRef.current || !videoDurationRef.current) return;

		const skipTo = Math.round(
			(event.offsetX / event.target.clientWidth) * parseInt(event.target.getAttribute('max'), 10),
		);

		if (seekRef.current) {
			seekRef.current.setAttribute('data-seek', skipTo);
		}

		const tooltipDate = new Date(startTimeRef.current.getTime() + skipTo * 1000);
		const formattedTooltipTime = formatTime12Hour(tooltipDate);

		const rect = playerRef.current?.getBoundingClientRect();
		if (seekTooltipRef.current && formattedTooltipTime && rect) {
			seekTooltipRef.current.textContent = formattedTooltipTime;
			seekTooltipRef.current.style.left = `${event.pageX - rect.left - 20}px`;
			seekTooltipRef.current.style.top = `-30px`;
		}
	}

	const skipAhead = useCallback(
		debounce((event) => {
			const video = playerRef.current;
			const skipTo = event.target.dataset.seek ? parseFloat(event.target.dataset.seek) : parseFloat(event.target.value);

			if (video && !isNaN(skipTo) && isFinite(skipTo)) {
				video.currentTime = skipTo;
				progressBarRef.current.value = skipTo;
				seekRef.current.value = skipTo;
			} else {
				console.error('Invalid time value: ' + skipTo);
			}
		}, 300),
		[],
	);

	useEffect(() => {
		if (startTime) {
			const [startHours, startMinutes, startSeconds] = startTime.split(':').map(Number);
			const startDate = new Date();
			startDate.setHours(startHours, startMinutes, startSeconds, 0);
			setDisplayStartTime(startDate);
			startTimeRef.current = startDate;
		}
	}, [startTime]);

	useEffect(() => {
		if (displayStartTime) {
			initializeVideo();
		}
	}, [displayStartTime]);

	function skipBack(seconds) {
		const video = playerRef.current;
		if (video) {
			video.currentTime = Math.max(video.currentTime - seconds, 0);
			updateTimeElapsed();
			updateProgress();
		}
	}

	return playerRef?.current ? (
		<div className="video-controls z-99" id={`video-controls-${room?.id}`}>
			<div
				className="flex flex-col gap-4 px-16 py-16 transition-all w-full transform"
				style={{
					backgroundImage: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.5))',
					transition: 'all .3s ease',
					transform: showControls ? 'translateY(0)' : 'translateY(100%)',
				}}
			>
				<div className="video-progress flex-grow">
					<progress defaultValue={0} id={`progress-bar-${room?.id}`} min="0" ref={progressBarRef} />
					<input
						className="seek"
						defaultValue={0}
						id={`seek-${room?.id}`}
						min="0"
						ref={seekRef}
						step="1"
						type="range"
					/>
					<div className="seek-tooltip" id={`seek-tooltip-${room?.id}`} ref={seekTooltipRef}>
						00:00
					</div>
				</div>

				<div className="bottom-controls gap-4">
					<div className="text-white flex items-center gap-8">
						<button
							data-title={playerRef?.current?.paused ? 'Play (k)' : 'Pause (k)'}
							id={`play-${room?.id}`}
							onClick={togglePlay}
							type="button"
						>
							<div className="">
								{playerRef?.current?.paused ? <PlayIcon className="size-24" /> : <PauseIcon className="size-24" />}
							</div>
						</button>

						<div className="flex items-center gap-16">
							<button
								className="volume-button"
								data-title={playerRef?.current?.muted || playerRef?.current?.volume === 0 ? 'Unmute (m)' : 'Mute (m)'}
								id={`volume-button-${room?.id}`}
								onClick={toggleMute}
								type="button"
							>
								<div>
									{playerRef?.current?.muted || playerRef?.current?.volume === 0 ? (
										<MuteIcon className="size-24" />
									) : (
										<VolumeIcon className="size-24" isLowVolume={playerRef?.current?.volume <= 0.5} />
									)}
								</div>
							</button>

							<input
								className="volume rounded-lg hidden sm:block"
								data-mute="0.5"
								id={`volume-${room?.id}`}
								max="1"
								min="0"
								onChange={updateVolume}
								ref={volumeRef}
								step="0.01"
								type="range"
								value={volume}
							/>
						</div>

						<div className="time text-pd-xs md:text-pd-sm">
							<time id={`time-elapsed-${room?.id}`} ref={timeElapsedRef}>
								00:00
							</time>
							<span> / </span>
							<time id="duration" ref={durationRef}>
								00:00
							</time>
						</div>
					</div>
					<div className="flex items-center">
						<div className="flex items-center gap-16 text-white">
							<button className="" data-title="Skip back 30 seconds" onClick={() => skipBack(30)} type="button">
								<BackThirtyIcon className="size-24" />
							</button>
							<button className="" data-title="Skip back 60 seconds" onClick={() => skipBack(60)} type="button">
								<BackSixtyIcon className="size-24" />
							</button>
						</div>
						<div className="relative">
							<Button aria-haspopup="true" onClick={toggleSpeedMenu}>
								<SettingsIcon className="size-24 text-white" />
							</Button>
							{isSpeedMenuOpen ? (
								<div className={classes.customMenu}>
									<div className="flex flex-col ">
										<p className="mb-8 px-16">Playback Speed</p>
										{playbackSpeeds.map((speedOption) => {
											const isSelected = playbackSpeed === speedOption.value;
											return (
												<button
													className={classes.menuOption}
													key={speedOption.value}
													onClick={() => handleSpeedSelect(speedOption.value)}
													type="button"
												>
													{isSelected ? <Check className={classes.menuCheckIcon} /> : <div className="w-24" />}
													<span>{speedOption.name}</span>
												</button>
											);
										})}
									</div>
								</div>
							) : null}
						</div>

						<button
							className="fullscreen-button"
							data-title="Full screen (f)"
							id={`fullscreen-button-${room?.id}`}
							ref={fullScreenRef}
							type="button"
						>
							<div className="text-white">
								<span ref={fullscreenIconRef}>
									<MaximizeIcon className="size-24" />
								</span>
								<span className="hidden" ref={fullscreenExitIconRef}>
									<MinimizeIcon className="size-24" />
								</span>
							</div>
						</button>
					</div>
				</div>
			</div>
		</div>
	) : null;
};

export default KinesisHistoryControls;
